import React from 'react';
import { Paths } from './shared/url_helpers';
import { flowRight } from 'lodash';

import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import {
  CoreCheckoutSelectCreditCard,
  CoreCheckoutDeleteCreditCard,
  CheckoutTrackingFields,
  core_apimessages_CheckoutPaymentMethod_Type as PaymentMethodType,
} from '../schemas/rql';

import ModalDialog from '@reverbdotcom/commons/src/components/modal_dialog';
import { WithRouterProps } from 'react-router';
import { RCAlertBox } from '@reverbdotcom/cadence/components';
import { CreditCardFormContext, useCreditCardFormState } from './credit_card_form_context';
import {
  CHECKOUT_SELECT_CREDIT_CARD_MUTATION,
  SelectCheckoutCreditCardMutationFn,
  useSelectCheckoutCreditCard,
} from './checkout_select_credit_card_hook';
import {
  buildModalState,
  ModalPage,
} from './credit_card_wallet_modal_state';
import { buildCheckoutClickEvent } from '../lib/checkoutTracking';
import { CreditCardWalletClickInteraction } from '@reverbdotcom/commons/src/elog/mparticle_types';

import { useMutation } from '../lib/use_mutation_hook';
import gql from 'graphql-tag';
import { MutationFn } from 'react-apollo';

interface ExternalProps {
  children: React.ReactNode;
}

interface MutationProps {
  selectCheckoutCreditCard: SelectCheckoutCreditCardMutationFn;
  deleteCheckoutCreditCard: DeleteCheckoutCreditCardMutationFn;
}

type Checkout = CheckoutTrackingFields.Fragment;

type Props = MutationProps & WithRouterProps & ExternalProps;

type DeleteCheckoutCreditCardMutationFn = MutationFn<CoreCheckoutDeleteCreditCard.Mutation, CoreCheckoutDeleteCreditCard.Variables>;

const formId = 'credit-card-modal-form';

export function CreditCardWalletModal(props: Props) {
  const { query } = props.location;
  const selectedCardId = query.selected_card_id || props.params.creditCardId;

  const checkoutData: Checkout = {
    id: props.params.id,
    paymentMethod: {
      type: PaymentMethodType.DIRECT_CHECKOUT,
    },
  };

  const checkoutClickEvent = buildCheckoutClickEvent(checkoutData);

  const modalPaths = buildCheckoutModalPaths(
    props.params.id,
    selectedCardId,
  );

  const modalState = buildModalState({
    modalPaths,
    router: props.router,
  });

  const PAGE_TO_CLICK_EVENT = {
    [ModalPage.Index]: CreditCardWalletClickInteraction.SelectCard,
    [ModalPage.Create]: CreditCardWalletClickInteraction.CreateCard,
    [ModalPage.Edit]: CreditCardWalletClickInteraction.UpdateCard,
  } as const;

  async function onSelect(creditCardId: string) {
    form.setLoading(true);
    const result = await selectCheckoutCreditCard({ creditCardId });

    const modalState = buildModalState({
      modalPaths,
      router: props.router,
    });

    if (result) {
      checkoutClickEvent(PAGE_TO_CLICK_EVENT[modalState.page]);
      modalState.cancel();
    } else {
      form.setLoading(false);
    }
  }

  const form = useCreditCardFormState({
    id: formId,
    onSelect,
  });

  const { selectCheckoutCreditCard, errors: selectCardErrors } = useSelectCheckoutCreditCard(
    props.params.id,
    props.selectCheckoutCreditCard,
  );

  function onRequestClose() {
    checkoutClickEvent(CreditCardWalletClickInteraction.CloseModal);
    modalState.cancel();
  }

  function onBack() {
    checkoutClickEvent(CreditCardWalletClickInteraction.Cancel);
    modalState.goBack();
  }

  const { onDelete, errors: deleteCardErrors } = useDeleteCardCallback(
    selectedCardId,
    props.deleteCheckoutCreditCard,
  );

  function useDeleteCardCallback(
    selectedCardId: string,
    deleteCheckoutCreditCard: DeleteCheckoutCreditCardMutationFn,
  ) {
    const [mutate, { errors }] = useMutation(deleteCheckoutCreditCard);

    if (modalState.page !== ModalPage.Edit) {
      return { errors };
    }

    async function onDelete() {
      form.setLoading(true);
      const result = await mutate({
        variables: {
          input: {
            id: selectedCardId,
          },
        },
        refetchQueries: ['Core_CreditCardWallet_Index'],
        awaitRefetchQueries: true,
      });

      if (result) {
        checkoutClickEvent(CreditCardWalletClickInteraction.DeleteCard);
        props.router.push(Paths.checkoutCreditCardWallet.expand({
          checkoutId: props.params.id,
        }));
      }
      form.setLoading(false);
    }

    return { onDelete, errors };
  }

  const errors = selectCardErrors.concat(deleteCardErrors);

  return (
    <ModalDialog
      headerTitle={modalState.headerTitle}
      saveButtonText={modalState.saveButtonText}
      backButtonText={modalState.backButtonText}
      isSaving={form.isLoading}
      onRequestClose={onRequestClose}
      onNavigateBack={onBack}
      formId={form.id}
      containsFormElement
      isOpen
      onDelete={onDelete}
    >
      {errors.length > 0 && (
        <div className="mb-1">
          <RCAlertBox type="error">
            {errors.map(e => <li key={`${e.field}-${e.message}`}>{e.message}</li>)}
          </RCAlertBox>
        </div>
      )}
      <CreditCardFormContext.Provider value={form}>
        {props.children}
      </CreditCardFormContext.Provider>
    </ModalDialog>
  );
}

function buildCheckoutModalPaths(checkoutId: string, creditCardId: string) {
  const start = Paths.checkoutShow.expand({ checkoutId });
  const index = Paths.checkoutCreditCardWallet.expand({
    checkoutId,
    selected_card_id: creditCardId,
  });
  const create = Paths.checkoutCreateCreditCard.expand({ checkoutId });
  const edit = Paths.checkoutEditCreditCard.expand({ checkoutId, creditCardId });

  return {
    start,
    index,
    create,
    edit,
  };
}

const withDeleteCreditCardMutation = withGraphql<Props, CoreCheckoutDeleteCreditCard.Mutation, CoreCheckoutDeleteCreditCard.Variables>(
  gql`
  mutation Core_Checkout_DeleteCreditCard(
    $input: Input_core_apimessages_DeleteMyCreditCardRequest
  ) {
    deleteMyCreditCard(input: $input) {
      id
    }
  }
  `,
  {
    name: 'deleteCheckoutCreditCard',
  },
);

const withSelectCreditCardMutation = withGraphql<Props, CoreCheckoutSelectCreditCard.Mutation, CoreCheckoutSelectCreditCard.Variables>(
  CHECKOUT_SELECT_CREDIT_CARD_MUTATION,
  {
    name: 'selectCheckoutCreditCard',
  },
);

export default flowRight([
  withSelectCreditCardMutation,
  withDeleteCreditCardMutation,
])(CreditCardWalletModal);
